/**
 * Soft!=Live Page205 — Settings Models residual liquid (Page152/114/56 + dual).
 * Mount: /settings/models
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_models_status · mine/tenant catalogue · keys · byok
 * Soft residual deleted:
 *  - Soft invent seats while posture loading
 *  - Soft invent labels when AI platform is off
 *  - Soft mine/tenant success invent when bus sealed
 *  - path /operation labels painted in DOM
 * Platform catalogue stays under SA models (authority split).
 */
import { useState } from "react";
import { html, Ico, Card, Field, Input, Alert, go, KV } from "./ui.js";
import { path, useApi, api } from "./api.js";
import { useSession } from "./session.js";
import { envelopeSummary } from "./action-envelope.js";
import { Receipt } from "./layered-console.js";
import { LocalComputerPanel } from "./local-computer-panel.js";
import {
  JOB_LABEL,
  JOB_PURPOSE,
  kindOf,
  useByok,
  useCatalogue,
  useEffective,
  useJobModel,
  useModelWrites,
} from "./models-api.js";


/** Path-free operator copy for Models desk. Catalogue stays empty until Live says so. */
export function softModelSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot change model access here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|label|provider|key/.test(low)) {
    return "That model change was not accepted. Check the fields and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|ai_platform|platform/.test(low)) {
    return "Model services are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/ai\/models|\/settings\/models|GET |POST |PUT |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}



/** Soft≠Live Page205: missing contract ops must not crash the desk. */
function safeModelsPath(op) {
  try {
    return path(op);
  } catch (_e) {
    if (op === "get__settings_models_status") return "/settings/models/status";
    if (op === "get__ai_models_status") return "/ai/models/status";
    return "";
  }
}

/** Pure: seat chips from Live Settings Models posture (Page205). */
export function modelSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const mineMax = Number(lim.mine_paint_max);
  const tenMax = Number(lim.tenant_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    mineAllowed: p.mine_catalogue_allowed === true,
    mineWriteAllowed: p.mine_write_allowed === true,
    purposeAllowed: p.purpose_preference_allowed === true,
    tenantOpsAllowed: p.tenant_ops_allowed === true,
    platformSeat: p.platform_admin_seat === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim(),
    minePaintMax: Number.isFinite(mineMax) && mineMax > 0 ? Math.floor(mineMax) : 40,
    tenantPaintMax: Number.isFinite(tenMax) && tenMax > 0 ? Math.floor(tenMax) : 40,
  };
}

/** Pure: KPI strip from mine catalogue + effective. */
export function modelAccessKpisOf(mineModels, effective, byokPolicy) {
  const models = Array.isArray(mineModels) ? mineModels : [];
  const ready = models.filter((m) => m && (m.has_secret || m.provider === "ollama")).length;
  const eff = effective && typeof effective === "object" ? effective : null;
  const byok = byokPolicy && typeof byokPolicy === "object" ? byokPolicy : null;
  return {
    granted: models.length,
    ready,
    inUse: eff && eff.label ? String(eff.label) : "default",
    byokOn: !!(byok && byok.effective),
  };
}

function brainMatches(m, q, facet) {
  const kind = kindOf(m);
  if (facet === "local" && kind !== "local") return false;
  if (facet === "api" && kind === "local") return false;
  const n = String(q || "").trim().toLowerCase();
  if (!n) return true;
  const blob = [m.label, m.text_model, m.vision_model, m.provider, m.model_id, m.via]
    .join(" ").toLowerCase();
  return blob.includes(n);
}

function tagMatches(m, q) {
  const n = String(q || "").trim().toLowerCase();
  if (!n) return true;
  return `${m.name || ""} ${m.parameter_size || ""}`.toLowerCase().includes(n);
}

/** Tags list is Discover only. Empty URL auto-scans this computer. :9240 scheduler and :11434 Ollama are not live inference. */
export function canDiscoverLocalTags({ provider, baseUrl, costClass } = {}) {
  const p = String(provider || "").toLowerCase();
  const u = String(baseUrl || "").toLowerCase();
  const c = String(costClass || "").toLowerCase();
  if (!String(baseUrl || "").trim()) return true;
  return p.includes("ollama") || c === "local" || /:11434\b/.test(u) || /:9240\b/.test(u);
}

function discoverHonestyNote(tags) {
  const t = tags && typeof tags === "object" ? tags : {};
  if (t.loading || !t.ok) return "";
  const reason = String(t.reason || "").trim();
  return "Tags listed · not live inference"
    + (reason ? " · " + reason : "")
    + (t.live_inference ? " · listed only" : "");
}

function LocalTagPicker({
  tags, query, onQuery, picked, onToggle, onPickOne, onPublishMany, busy, mode,
}) {
  const rows = (tags.models || []).filter((m) => tagMatches(m, query));
  const selectedN = rows.filter((m) => picked && picked[m.name]).length;
  if (!tags.models || !tags.models.length) return null;
  return html`<div className="models-desk__tags" data-cmc-local-tags="1">
    <div className="models-desk__find">
      <input className="inp" value=${query} onInput=${(e) => onQuery(e.target.value)}
        placeholder="Search local models"/>
      ${mode === "multi" ? html`<button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => rows.forEach((m) => onToggle(m.name, true))}>Select visible</button>` : null}
      ${mode === "multi" && selectedN ? html`<button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => rows.forEach((m) => onToggle(m.name, false))}>Clear</button>` : null}
    </div>
    <div className="models-desk__chips">
      ${rows.map((m) => html`<label key=${m.name} className="models-desk__chip"
        data-on=${mode === "multi" ? (picked[m.name] ? "1" : "0") : "0"}>
        ${mode === "multi" ? html`<input type="checkbox" checked=${!!picked[m.name]}
          onChange=${() => onToggle(m.name)}/>` : null}
        <button type="button" className=${"btn btn--ghost btn--sm" + ((mode !== "multi" && onPickOne) ? "" : "")}
          onClick=${() => (mode === "multi" ? onToggle(m.name) : onPickOne(m))}>
          ${m.name}${m.parameter_size ? ` · ${m.parameter_size}` : ""}
        </button>
      </label>`)}
    </div>
    ${!rows.length ? html`<p className="note">No local model matches</p>` : null}
    ${discoverHonestyNote(tags) ? html`<p className="note" data-cmc-discover-honesty="1">${discoverHonestyNote(tags)}</p>` : null}
    ${mode === "multi" ? html`<button type="button" className="btn btn--go btn--sm"
      disabled=${busy || !selectedN} onClick=${onPublishMany}>
      ${busy ? "Publishing…" : `Publish selected (${selectedN})`}</button>` : null}
  </div>`;
}

function PurposePrefs({ models, writes, toast, allowed }) {
  const [purposeQ, setPurposeQ] = useState("");
  const design = useJobModel("design");
  const clarify = useJobModel("clarify");
  const review = useJobModel("review");
  const summarise = useJobModel("summarise");
  const onboard = useJobModel("onboard");
  const rows = [
    ["design", design],
    ["clarify", clarify],
    ["review", review],
    ["summarise", summarise],
    ["onboard", onboard],
  ];
  const choices = (models || []).filter((m) => brainMatches(m, purposeQ, "all"));

  const pick = async (jobId, modelId) => {
    if (!allowed) {
      toast && toast("Purpose routing is not available on this seat.", "warn");
      return;
    }
    const purpose = JOB_PURPOSE[jobId] || "";
    const r = await writes.setPreference(purpose, modelId);
    if (r) {
      const hit = rows.find(([id]) => id === jobId);
      if (hit) hit[1].reload();
      toast((JOB_LABEL[jobId] || purpose) + " → " + (modelId || "default"), "go");
    } else toast(softModelSettingsErrorOf(writes.error) || "Could not set purpose.", "bad");
  };

  if (!allowed) {
    return html`
      <${Card} title="By purpose" sub="Purpose bus sealed · seat sealed.">
        <${Alert} tone="warn" title="Purpose preference sealed"
          say="Cannot invent job routing without mine catalogue seat."/>
      <//>`;
  }

  return html`
    <${Card} title="By purpose">
      <div className="models-desk__find">
        <input className="inp" value=${purposeQ} onInput=${(e) => setPurposeQ(e.target.value)}
          placeholder="Search brains"/>
      </div>
      <div className="mdl">
        ${rows.map(([jobId, job]) => html`
          <div key=${jobId} className="mdl__r" data-kind="purpose">
            <span className="mdl__k">${job.provider.purpose}</span>
            <span className="mdl__t">
              <b>${JOB_LABEL[jobId] || jobId}</b>
              <code>${job.provider.label}</code>
              <em>${job.loading ? "loading…" : (job.effective ? "purpose preference" : "using default")}</em>
            </span>
            <select className="inp" style=${{ maxWidth:220 }}
                value=${(job.effective && job.effective.model_id) || ""}
                onChange=${(e)=>pick(jobId, e.target.value)}>
              <option value="">Default preference</option>
              ${choices.map(m => html`
                <option key=${m.model_id} value=${m.model_id}>${m.label}${kindOf(m) === "local" ? " · local" : " · API"}</option>`)}
            </select>
          </div>`)}
      </div>
    <//>`;
}

export function ModelSettingsDesk({ toast, embedded }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";

  const posture = useApi(safeModelsPath("get__settings_models_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed (do not invent from client features).
  const seat = modelSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const postureLabel = seat.postureLabel
    || (seat.enabled
      ? (seat.tenantOpsAllowed
        ? "Catalogue open · account publish open"
        : "Catalogue open · account publish sealed")
      : "AI model platform is off on this deployment");

  const armed = seat.enabled || seat.armed;
  const isAdmin = seat.tenantOpsAllowed;
  const isStaff = seat.platformSeat || role === "superadmin" || role === "sa_staff";

  const mine = useCatalogue(seat.mineAllowed ? "mine" : null);
  const effective = useEffective(seat.mineAllowed ? "" : false);
  const writes = useModelWrites("user");
  const tenant = useCatalogue(seat.tenantOpsAllowed ? "tenant" : null);
  const tenantWrites = useModelWrites("tenant");
  const byok = useByok(seat.tenantOpsAllowed || seat.mineAllowed ? "tenant" : null);

  const [add, setAdd] = useState(false);
  const [draft, setDraft] = useState({
    label: "", provider: "openai_compat", base_url: "", text_model: "", api_key: "",
  });
  const [tenantDraft, setTenantDraft] = useState({
    label: "Account default",
    provider: "openai_compat",
    base_url: "",
    text_model: "",
    cost_class: "small",
    api_key: "",
  });
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [probeBusyId, setProbeBusyId] = useState("");
  const [localTags, setLocalTags] = useState({
    loading: false, models: [], catalog: [], runtimes: [], reason: "", host: "",
    ok: false, live_inference: false, chosen_base: "", pull_capable: false,
  });
  const [tagQ, setTagQ] = useState("");
  const [pickedLocal, setPickedLocal] = useState({});
  const [brainQ, setBrainQ] = useState("");
  const [brainFacet, setBrainFacet] = useState("all");
  const [brainLane, setBrainLane] = useState("answer");

  async function discoverLocalTags(baseUrl, opts) {
    const url = String(baseUrl || "").trim();
    const silent = !!(opts && opts.silent);
    if (!silent) {
      setLocalTags({ loading: true, models: [], catalog: [], runtimes: [], reason: "", host: "", ok: false, live_inference: false, chosen_base: "" });
    }
    try {
      const r = await api("/ai/models/local-runtime/tags", {
        method: "POST",
        body: url ? { base_url: url } : {},
      });
      const models = (r && r.models) || [];
      const liveInf = !!(r && r.live_inference);
      const pack = {
        loading: false,
        models,
        catalog: (r && r.catalog) || [],
        runtimes: (r && r.runtimes) || [],
        reason: String((r && r.reason) || ""),
        host: String((r && r.base_host) || ""),
        ok: !!(r && r.ok),
        live_inference: liveInf,
        chosen_base: String((r && r.chosen_base) || url || ""),
        pull_capable: !!(r && r.pull_capable),
      };
      setLocalTags(pack);
      if (silent) return pack;
      if (r && r.ok) {
        const n = Array.isArray(models) ? models.length : 0;
        const c = Array.isArray(r.catalog) ? r.catalog.length : 0;
        toast && toast(
          n || c
            ? `Found ${n} installed · ${c} listed · not live inference`
            : "Reachable — 0 tags (pull a model first) · not live inference",
          liveInf ? "bad" : "warn",
        );
      } else toast && toast(softModelSettingsErrorOf((r && r.reason) || "discover_failed") || "Could not list local models", "bad");
      return pack;
    } catch (e) {
      setLocalTags({ loading: false, models: [], catalog: [], runtimes: [], reason: "transport", host: "", ok: false, live_inference: false, chosen_base: "" });
      if (!silent) toast && toast(softModelSettingsErrorOf(e) || "Local discover failed", "bad");
      return null;
    }
  }

  const mineModels = (mine.models || []).slice(0, seat.minePaintMax);
  const tenantModels = (tenant.models || []).slice(0, seat.tenantPaintMax);
  const catalogueFilter = !embedded || mineModels.length > 4;
  const visibleMine = mineModels.filter((m) => !catalogueFilter || brainMatches(m, brainQ, brainFacet));
  const visibleTenant = tenantModels.filter((m) => !catalogueFilter || brainMatches(m, brainQ, brainFacet));
  const toggleLocal = (name, force) => setPickedLocal((p) => ({
    ...p,
    [name]: force === true ? true : (force === false ? false : !p[name]),
  }));
  const kpis = modelAccessKpisOf(mineModels, effective.effective, byok.policy);
  const sot = (post.product_sot && typeof post.product_sot === "object") ? post.product_sot : {};

  const reloadAll = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    mine.reload && mine.reload();
    effective.reload && effective.reload();
    byok.reload && byok.reload();
    tenant.reload && tenant.reload();
    toast && toast("Model access refreshed", "go");
  };

  if (!armed) {
    return html`<div className="models-desk models-desk--liquid"
      data-cmc-page="page114-models" data-cmc-models="page56" data-cmc-residual="page205-models" data-cmc-models152="page152-models">
      <div className="models-desk__rail">
        <div>
          <p className="eyebrow">Settings · Model access</p>
          <h2 className="models-desk__title">Model platform off</h2>
          <p className="note">${postureLabel}. Model rows stay empty while the platform is off.</p>
        </div>
        <div className="btn-row models-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reloadAll}
            disabled=${posture.loading || !seat.reReadAllowed}>
            <${Ico} d="refresh" w=${13}/> Refresh
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/settings/ai")}>
            AI & decisions
          </button>
        </div>
      </div>
      <${Alert} tone="info" title="Catalogue and account publish seats"
        say=${`mine=${seat.mineAllowed ? "open" : "sealed"} · tenant=${seat.tenantOpsAllowed ? "open" : "sealed"} · ${seat.role || "—"}`}/>
      <${Alert} tone="warn" title="AI model platform off"
        say="The AI model platform is off. Catalogues stay unmounted until the deployment arms it. Rows and balances only from the server."/>
      <${Card} title="Models posture" meta="deployment"
          sub="Posture bus — Ready labels only from server status.">
        ${posture.error ? html`<${Alert} tone="warn" title="Status refused" say=${softModelSettingsErrorOf(posture.error)}/>` : null}
        <div className="models-desk__kpis">
          <div className="models-desk__kpi" data-on="0">
            <span>Flag</span>
            <b>${posture.loading ? "…" : "off"}</b>
            <em>deployment</em>
          </div>
          <div className="models-desk__kpi" data-on="0">
            <span>Mine bus</span>
            <b>sealed</b>
            <em>Live</em>
          </div>
          <div className="models-desk__kpi" data-on="0">
            <span>Tenant write</span>
            <b>no</b>
            <em>seat honesty</em>
          </div>
          <div className="models-desk__kpi" data-on="0">
            <span>Your seat</span>
            <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
            <em>Server seat</em>
          </div>
        </div>
      <//>
    </div>`;
  }

  const pick = async (modelId) => {
    if (!seat.mineWriteAllowed) {
      toast && toast("Saving a personal key is not available on this seat.", "warn");
      return;
    }
    const r = await writes.setPreference("", modelId);
    if (r) {
      effective.reload();
      toast("Now using " + (modelId || "the default"), "go");
    } else toast(softModelSettingsErrorOf(writes.error) || "Could not set that preference.", "bad");
  };

  const saveMine = async () => {
    if (!seat.mineWriteAllowed) {
      toast && toast("Saving a personal key is not available on this seat.", "warn");
      return;
    }
    if (!draft.label.trim() || !draft.provider.trim()) {
      return toast("Label and provider are required.", "warn");
    }
    const r = await writes.save({
      label: draft.label.trim(),
      provider: draft.provider.trim(),
      base_url: draft.base_url.trim(),
      text_model: draft.text_model.trim(),
      api_key: draft.api_key || undefined,
      is_default: true,
    });
    if (r) {
      setAdd(false);
      setDraft({ label: "", provider: "openai_compat", base_url: "", text_model: "", api_key: "" });
      mine.reload();
      toast("Your key is stored", "go");
    } else toast(softModelSettingsErrorOf(writes.error) || "Could not save the key.", "bad");
  };

  const publishTenant = async () => {
    if (!seat.tenantOpsAllowed) {
      toast && toast("Account model publish is not available on this seat.", "warn");
      return;
    }
    if (!tenantDraft.label.trim()) return toast("A label is required.", "warn");
    const r = await tenantWrites.save({
      label: tenantDraft.label.trim(),
      provider: tenantDraft.provider.trim() || "openai_compat",
      base_url: tenantDraft.base_url.trim(),
      text_model: tenantDraft.text_model.trim(),
      cost_class: tenantDraft.cost_class || "small",
      api_key: tenantDraft.api_key || undefined,
      is_default: true,
    });
    if (r) {
      setShowTenantForm(false);
      setTenantDraft({
        label: "Account default",
        provider: "openai_compat",
        base_url: "",
        text_model: "",
        cost_class: "small",
        api_key: "",
      });
      tenant.reload();
      effective.reload && effective.reload();
      toast("Account brain published — users inherit it", "go");
      setReceipt({
        title: "Brain published",
        accepted: tenantDraft.label.trim(),
        effective: "Account tree inherits",
        evidence: "Live tenant catalogue write · " + String(tenantDraft.provider || ""),
        at: new Date().toISOString(),
        next: "Test connection, then verify readiness on AI Control.",
        tone: "go",
      });
      setShowTenantForm(false);
    } else toast(softModelSettingsErrorOf(tenantWrites.error) || "Could not publish that model.", "bad");
  };

  const publishPickedLocals = async () => {
    if (!seat.tenantOpsAllowed) {
      toast && toast("Account model publish is not available on this seat.", "warn");
      return;
    }
    const names = Object.keys(pickedLocal).filter((n) => pickedLocal[n]);
    if (!names.length) return toast && toast("Select one or more local models first.", "warn");
    const url = String(tenantDraft.base_url || draft.base_url || localTags.chosen_base || "").trim();
    if (!url) return toast && toast("Click Local first so we can find Ollama on this computer.", "warn");
    let ok = 0;
    let lastErr = "";
    for (const name of names) {
      const r = await tenantWrites.save({
        label: name,
        provider: "ollama",
        base_url: url,
        text_model: name,
        cost_class: "local",
        is_default: ok === 0,
      });
      if (r) ok += 1;
      else lastErr = tenantWrites.error;
    }
    if (ok) {
      tenant.reload();
      effective.reload && effective.reload();
      setPickedLocal({});
      setReceipt({
        title: "Local brains published",
        accepted: ok + " model(s)",
        effective: url,
        evidence: "Live Ollama tags → tenant catalogue",
        at: new Date().toISOString(),
        next: "Confirm the live brain, then list tools if you need them.",
        tone: "go",
      });
      toast(`${ok} local model(s) published — users inherit them`, "go");
    }
    if (ok < names.length) {
      toast(softModelSettingsErrorOf(lastErr) || "Some local models were not published.", "bad");
    }
  };

  const brainLanes = html`<nav className="models-desk__lanes" aria-label="Model access" data-cmc-brain-lanes="1"
      data-cmc-brain-lane=${brainLane}>
      ${[
        ["answer", "Answer", "Which brain"],
        ["purpose", "Purpose", "One job"],
        ["publish", "Publish", "Account brain"],
        ["limits", "Limits", "What stays"],
      ].map(([id, label]) => html`
        <button type="button" key=${id} className="models-desk__lane"
          data-on=${brainLane === id ? "1" : "0"}
          onClick=${() => setBrainLane(id)}>
          <b>${label}</b>
        </button>`)}
    </nav>`;
  const brainActs = html`<div className="btn-row models-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reloadAll}
          disabled=${mine.loading || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
        ${embedded ? null : html`<button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/settings/ai")}>
          AI & decisions
        </button>`}
        ${isStaff ? html`
          <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/sa/models")}>
            Platform models
          </button>` : null}
      </div>`;

  return html`<div className="models-desk models-desk--liquid"
    data-cmc-page="page114-models" data-cmc-models="page56" data-cmc-residual="page205-models" data-cmc-models152="page152-models"
    data-cmc-ai-brains=${embedded ? "embedded" : "page"}
    data-cmc-models-receipt=${receipt ? "1" : "0"}>
    ${receipt ? html`<${Receipt} title=${receipt.title} accepted=${receipt.accepted}
      effective=${receipt.effective} evidence=${receipt.evidence} at=${receipt.at}
      next=${receipt.next} tone=${receipt.tone || "go"}/>` : null}
    <div className=${"models-desk__rail" + (embedded ? " models-desk__rail--inline" : "")}
      data-cmc-models-rail=${embedded ? "acts" : "full"}>
      ${embedded ? brainLanes : html`<div>
        <p className="eyebrow">Settings · Model access</p>
        <h2 className="models-desk__title">Admin brain · Cloud or Local</h2>
        <p className="note">
          ${postureLabel}. Pick which brain answers. Publish and limits are separate lanes. Tool servers are not chosen here.
        </p>
      </div>`}
      ${brainActs}
    </div>
    ${embedded ? null : brainLanes}

    ${(writes.envelope || tenantWrites.envelope)
      ? html`<${KV} k="Last action"
          v=${envelopeSummary(writes.envelope || tenantWrites.envelope) || "—"}
          tone=${(writes.envelope || tenantWrites.envelope).ok ? "hi" : "warn"}/>`
      : null}

    ${brainLane === "limits" ? html`
    <${Alert} tone="info" title="How model access works"
      say=${`Admin configures models: ${sot.admin_configures_models !== false ? "yes" : "no"}. Users inherit: ${sot.user_inherits_admin_tree !== false ? "yes" : "no"}. Custom cloud: ${sot.custom_cloud_allowed !== false ? "yes" : "no"}. Evidence stays local. Seat: ${seat.role || "—"}.`}/>
    <${Alert} tone="warn" title="Local AI"
      say="Ollama / LAN reach via Test connection stays practice until digest is installed and runtime is healthy. Pull miss → local_pull_miss · never claim on-site local inference. Industry pitfall we avoid: green 'ready' while weights are still downloading."/>
    <${Alert} tone="info" title="Who can change models"
      say=${`${seat.mineAllowed ? "Personal catalogue is open." : "Personal catalogue is closed."} ${seat.tenantOpsAllowed ? "Account publish is open." : "Account publish is closed."} ${seat.mineWriteAllowed ? "Personal write is open." : "Personal write is closed."}`}/>
    ` : null}

    ${embedded && brainLane === "answer" ? html`<${LocalComputerPanel} toast=${toast} seat=${seat} tenantWrites=${tenantWrites}
      tags=${localTags} picked=${pickedLocal} onToggle=${toggleLocal}
      busy=${tenantWrites.busy || localTags.loading}
      onTags=${() => discoverLocalTags("")}
      onPublished=${(ok, url) => {
        tenant.reload();
        effective.reload && effective.reload();
      }}/>` : null}

    ${brainLane === "answer" && catalogueFilter ? html`<div className="models-desk__find" data-cmc-brain-find="1">
      <input className="inp" value=${brainQ} onInput=${(e) => setBrainQ(e.target.value)}
        placeholder="Search local and API brains"/>
      <div className="models-desk__facets" role="group" aria-label="Brain kind">
        ${[["all", "All"], ["local", "Local"], ["api", "API"]].map(([id, label]) => html`
          <button type="button" key=${id} className="btn btn--ghost btn--sm"
            data-on=${brainFacet === id ? "1" : "0"}
            data-cmc-brain-facet=${id}
            onClick=${() => {
              setBrainFacet(id);
              if (id === "local") discoverLocalTags("");
            }}>${label}</button>`)}
      </div>
    </div>

    ${!embedded && (brainFacet === "local" || localTags.loading || localTags.ok) ? html`
      <${LocalComputerPanel} toast=${toast} seat=${seat} tenantWrites=${tenantWrites}
        tags=${localTags} picked=${pickedLocal} onToggle=${toggleLocal}
        busy=${tenantWrites.busy || localTags.loading}
        onTags=${(opts) => discoverLocalTags(tenantDraft.base_url || draft.base_url || "", opts)}
        onPublished=${(ok, url) => {
          tenant.reload();
          effective.reload && effective.reload();
          setReceipt({
            title: "Local brains for AI Team",
            accepted: ok + " model(s)",
            effective: url,
            evidence: "Installed local tags → tenant catalogue",
            at: new Date().toISOString(),
            next: "AI Team uses this brain on the next tick.",
            tone: "go",
          });
        }}/>` : null}` : null}

    ${brainLane !== "answer" ? null : !seat.mineAllowed ? html`
      <${Alert} tone="warn" title="Mine catalogue sealed"
        say="Personal models only from the account catalogue."/>`
    : html`
    <${Card} ai title="What will answer for me"
        meta=${effective.effective ? effective.effective.label : "nothing pinned yet"}>
      ${mine.loading ? html`<p className="note">Loading mine catalogue…</p>` : null}
      ${mine.error ? html`<${Alert} tone="bad" title="Mine catalogue refused" say=${softModelSettingsErrorOf(mine.error)}/>` : null}
      <div className="mdl">
        ${visibleMine.map((m) => html`
          <div key=${m.model_id} className="mdl__r" data-kind=${kindOf(m)}
               data-state=${m.has_secret || m.provider === "ollama" ? "ready" : "key_missing"}>
            <span className="mdl__k">${kindOf(m) === "local" ? "on your hardware" : "hosted"}</span>
            <span className="mdl__t">
              <b>${m.label}</b>
              <code>${m.text_model || m.vision_model || m.provider}</code>
              <em>via ${m.via}${m.is_default ? " · account default" : ""}</em>
            </span>
            <span className="mdl__s" data-state=${m.has_secret || m.provider === "ollama" ? "ready" : "key_missing"}>
              ${effective.effective && effective.effective.model_id === m.model_id ? "in use" : "available"}
            </span>
            <button type="button" className="btn btn--ghost btn--sm"
                disabled=${!!(effective.effective && effective.effective.model_id === m.model_id) || writes.busy || !seat.mineWriteAllowed}
                onClick=${()=>pick(m.model_id)}>Use this</button>
          </div>`)}
        ${!mine.loading && !visibleMine.length ? html`
          <${Alert} tone="warn" title=${mineModels.length ? "No match" : "Nothing granted yet"}
            say=${mineModels.length
              ? "Nothing matches this search. Clear Local / API or the search box."
              : "Ask your account owner to publish a Cloud API key or Local AI. Empty stays empty until your account grants them."}/>` : null}
      </div>
    <//>

    ${seat.mineWriteAllowed && byok.policy && byok.policy.effective ? html`
    <${Card} title="Optional personal key (edge)"
        sub="Not the product default. Admin Cloud or Local wins. Only use this when bring-your-own-key is armed for rare edge cases.">
      <div className="btn-row">
        <button type="button" className="btn btn--ghost btn--sm"
            onClick=${()=>setAdd((a) => !a)}>
          <${Ico} d="plus" w=${13}/> ${add ? "Cancel" : "Register edge key"}
        </button>
      </div>
      ${add ? html`
        <div className="mdl__new models-desk__form">
          <div className="grid g2" style=${{ gap:"0 16px" }}>
            <${Field} label="What to call it">
              <${Input} icon="brain" value=${draft.label}
                onInput=${(e)=>setDraft((d)=>({ ...d, label: e.target.value }))} placeholder="Edge Claude"/><//>
            <${Field} label="Provider">
              <${Input} icon="chip" value=${draft.provider}
                onInput=${(e)=>setDraft((d)=>({ ...d, provider: e.target.value }))} placeholder="openai_compat"/><//>
            <${Field} label="Address" hint="https for hosted. Local Ollama → http://host.docker.internal:11434">
              <${Input} icon="link" value=${draft.base_url}
                onInput=${(e)=>setDraft((d)=>({ ...d, base_url: e.target.value }))} placeholder="https://api.example.invalid/v1"/><//>
            <${Field} label="Model name" hint="Pick from Discover when Local Ollama">
              <${Input} icon="chip" value=${draft.text_model}
                onInput=${(e)=>setDraft((d)=>({ ...d, text_model: e.target.value }))} placeholder="claude-sonnet-4"/><//>
              <div className="btn-row" style=${{ marginTop: 6 }}>
                <button type="button" className="btn btn--ghost btn--sm"
                  disabled=${localTags.loading || !canDiscoverLocalTags({
                    provider: draft.provider, baseUrl: draft.base_url,
                  })}
                  onClick=${() => discoverLocalTags(draft.base_url)}>
                  ${localTags.loading ? "Discovering…" : "Discover local models"}
                </button>
                ${localTags.host ? html`<span className="note">host ${localTags.host} · ${localTags.models.length} tag(s) · not live inference</span>` : null}
              </div>
              <${LocalTagPicker} tags=${localTags} query=${tagQ} onQuery=${setTagQ}
                picked=${pickedLocal} onToggle=${toggleLocal} mode="single"
                onPickOne=${(m) => setDraft((d) => ({
                  ...d,
                  provider: String(d.provider || "").toLowerCase().includes("ollama") ? d.provider : "ollama",
                  text_model: m.name,
                }))}/>
            <${Field} label="Key" hint="Write-only. Never shown again. Leave empty for Local Ollama.">
              <${Input} icon="lock" type="password" value=${draft.api_key}
                onInput=${(e)=>setDraft((d)=>({ ...d, api_key: e.target.value }))} placeholder="••••••••••••"/><//>
          </div>
          <button type="button" className="btn btn--sm"
            disabled=${writes.busy} onClick=${saveMine}>
            ${writes.busy ? "Saving…" : "Save edge key"}
          </button>
          ${writes.error ? html`<${Alert} tone="bad" title="Not saved" say=${softModelSettingsErrorOf(writes.error)}/>` : null}
        </div>` : null}
    <//>` : html`
      <${Alert} tone="info" title="Personal keys sealed"/>`}
    `}

    <div className="models-desk__kpis">
      <div className="models-desk__kpi" data-on=${kpis.granted ? "1" : "0"}>
        <span>Granted</span>
        <b>${seat.mineAllowed ? kpis.granted : "—"}</b>
        <em>personal</em>
      </div>
      <div className="models-desk__kpi" data-on=${kpis.ready ? "1" : "0"}>
        <span>Ready keys</span>
        <b>${seat.mineAllowed ? kpis.ready : "—"}</b>
        <em>honesty</em>
      </div>
      <div className="models-desk__kpi" data-on=${effective.effective ? "1" : "0"}>
        <span>In use</span>
        <b>${seat.mineAllowed ? kpis.inUse : "—"}</b>
        <em>effective</em>
      </div>
      <div className="models-desk__kpi" data-on=${kpis.byokOn ? "1" : "0"}>
        <span>BYOK (tenant)</span>
        <b>${isAdmin ? (kpis.byokOn ? "on" : "off") : "—"}</b>
        <em>${seat.tenantOpsAllowed ? "account publish open" : "account publish closed"}</em>
      </div>
    </div>

    ${brainLane === "purpose" ? html`<${PurposePrefs} models=${mineModels} writes=${writes} toast=${toast} allowed=${seat.purposeAllowed}/>` : null}

    ${brainLane !== "publish" ? null : seat.tenantOpsAllowed ? html`
      <${Card} title="Admin brain for this account"
          meta=${tenantModels.length + " published"}
          sub="Publish Cloud API key or Local AI (Ollama / LAN). The tree inherits. Test connection never echoes the key.">
        ${tenant.forbidden ? html`
          <${Alert} tone="warn" title="Admin required"
            say="Publishing for the account needs an admin (or staff) session."/>` : null}
        ${tenant.error && !tenant.forbidden ? html`
          <${Alert} tone="bad" title="Tenant catalogue refused" say=${softModelSettingsErrorOf(tenant.error)}/>` : null}
        <div className="mdl">
          ${visibleTenant.map((m) => html`
            <div key=${m.model_id} className="mdl__r" data-kind=${kindOf(m)} data-state="ready">
              <span className="mdl__k">${kindOf(m) === "local" ? "on your hardware" : "hosted"}</span>
              <span className="mdl__t">
                <b>${m.label}</b>
                <code>${m.text_model || m.provider}</code>
                <em>${m.is_default ? "account default" : "available"}</em>
              </span>
              <button type="button" className="btn btn--ghost btn--sm"
                  disabled=${tenantWrites.busy || probeBusyId === m.model_id}
                  onClick=${async ()=>{
                    setProbeBusyId(m.model_id);
                    try {
                      const r = await tenantWrites.probe(m.model_id);
                      if (!r) {
                        toast(softModelSettingsErrorOf(tenantWrites.error) || "Probe refused.", "bad");
                        return;
                      }
                      const reason = String(r.reason || "").trim() || (r.ok ? "ok" : "failed");
                      toast(
                        r.ok
                          ? `Test connection · ${reason}${r.live ? " · signed in" : " · reach only (weights not local yet)"}`
                          : `Test connection failed · ${reason}`,
                        r.ok ? (r.live ? "go" : "warn") : "warn",
                      );
                      if (kindOf(m) === "local" && r.ok && !r.live) {
                        try {
                          const digest = String(m.text_model || m.model_id || "").trim();
                          if (digest) {
                            await api("/ai/models/local-pull/jobs", {
                              method: "POST",
                              body: { admin_digest: digest },
                            });
                            toast("Pull job queued · weights download when the pipeline is ready", "warn");
                          }
                        } catch (_e) { /* Soft best-effort */ }
                      }
                    } finally {
                      setProbeBusyId("");
                    }
                  }}>${probeBusyId === m.model_id ? "Testing…" : "Test connection"}</button>
              <button type="button" className="btn btn--ghost btn--sm" disabled=${tenantWrites.busy}
                  onClick=${async ()=>{
                    const r = await tenantWrites.remove(m.model_id);
                    if (r) { tenant.reload(); toast("Removed " + m.label, "go"); }
                    else toast(softModelSettingsErrorOf(tenantWrites.error) || "Could not remove that model.", "bad");
                  }}>Remove</button>
            </div>`)}
        </div>
        <div className="btn-row" style=${{ marginTop:13 }}>
          <button type="button" className="btn btn--ghost btn--sm"
              onClick=${()=>setShowTenantForm((v)=>!v)}>
            <${Ico} d="plus" w=${13}/> ${showTenantForm ? "Cancel" : "Publish Cloud or Local"}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${tenantWrites.busy}
              onClick=${async ()=>{
                const next = !(byok.policy && byok.policy.allow_byok);
                const r = await tenantWrites.setByok(next);
                if (r) { byok.reload(); toast(next ? "Edge BYOK on (optional)" : "Edge BYOK off — Admin brain only", "go"); }
                else toast(softModelSettingsErrorOf(tenantWrites.error) || "Could not change bring-your-own-key.", "bad");
              }}>
            ${byok.policy && byok.policy.allow_byok ? "Seal edge BYOK" : "Arm edge BYOK"}
          </button>
        </div>
        ${showTenantForm ? html`
          <div className="mdl__new models-desk__form" style=${{ marginTop:12 }}>
            <div className="grid g2" style=${{ gap:"0 16px" }}>
              <${Field} label="Label">
                <${Input} value=${tenantDraft.label}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, label: e.target.value }))}/><//>
              <${Field} label="Provider" hint="openai_compat · custom_cloud · custom_<slug> · ollama · anthropic">
                <${Input} value=${tenantDraft.provider}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, provider: e.target.value }))}
                  placeholder="openai_compat or custom_acme"/><//>
              <${Field} label="Cloud or Local URL" hint="HTTPS cloud · http://host.docker.internal:11434 or LAN:11434">
                <${Input} value=${tenantDraft.base_url}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, base_url: e.target.value }))}
                  placeholder="http://host.docker.internal:11434"/><//>
              <${Field} label="Model name" hint="Pick from Discover when Local Ollama">
                <${Input} value=${tenantDraft.text_model}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, text_model: e.target.value }))}
                  placeholder="qwen2.5:1.5b"/><//>
              <div className="btn-row" style=${{ gridColumn: "1 / -1", marginTop: 4 }}>
                <button type="button" className="btn btn--ghost btn--sm"
                    disabled=${localTags.loading || !canDiscoverLocalTags({
                      provider: tenantDraft.provider,
                      baseUrl: tenantDraft.base_url,
                      costClass: tenantDraft.cost_class,
                    })}
                    onClick=${() => discoverLocalTags(tenantDraft.base_url)}>
                  ${localTags.loading ? "Discovering…" : "Discover local models"}
                </button>
                ${localTags.host ? html`<span className="note">host ${localTags.host} · ${localTags.models.length} tag(s) · not live inference</span>` : null}
              </div>
              <div style=${{ gridColumn: "1 / -1" }}>
                <label className="note">Installed on this endpoint — search, single-select, or multi-select then publish</label>
                <${LocalTagPicker} tags=${localTags} query=${tagQ} onQuery=${setTagQ}
                  picked=${pickedLocal} onToggle=${toggleLocal} mode="multi"
                  busy=${tenantWrites.busy} onPublishMany=${publishPickedLocals}
                  onPickOne=${(m) => setTenantDraft((d) => ({
                    ...d,
                    provider: "ollama",
                    cost_class: "local",
                    text_model: m.name,
                    label: d.label || m.name,
                  }))}/>
              </div>
              <${Field} label="Cloud API key" hint="Write-only · leave empty for Local Ollama">
                <${Input} type="password" value=${tenantDraft.api_key || ""}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, api_key: e.target.value }))}
                  placeholder="••••••••••••"/><//>
              <${Field} label="Cost class" hint="local · small · large">
                <${Input} value=${tenantDraft.cost_class || "small"}
                  onInput=${(e)=>setTenantDraft((d)=>({ ...d, cost_class: e.target.value }))}/><//>
            </div>
            <button type="button" className="btn btn--sm" disabled=${tenantWrites.busy} onClick=${publishTenant}>
              ${tenantWrites.busy ? "Publishing…" : "Publish for account tree"}
            </button>
          </div>` : null}
        ${byok.policy && byok.policy.platform_ceiling === false ? html`
          <${Alert} tone="warn" title="Platform ceiling is off"
            say="Staff disabled BYOK platform-wide. Tenant flip has no effect until they raise the ceiling."/>` : null}
      <//>` : html`
      <${Alert} tone="info" title="Account publish sealed"
        say="Account publish / BYOK flip need admin. Tenant catalogue sealed for this seat."/>`}

    ${brainLane === "limits" ? html`<${Card} title="What never leaves"
        sub="Independent of which model a request reaches.">
      ${[
        ["Keys", "Write-only on the way in. Never echoed back, never logged, never in a response."],
        ["Endpoints", "Spenders see a label, never the base URL — credentials can hide in a path."],
        ["AI first", "Team runs on-the-loop. Hold / Takeover is human interrupt — not Approve-every-step theater."],
        ["Evidence", "Operation and response packs stay local on the client. Server audit is thin index only."],
        ["Access", "Admin publishes Cloud API key or Local AI. Users inherit that effective brain."],
      ].map(([k, v]) => html`
        <div key=${k} className="verdict" data-ok="1">
          <span className="verdict__st"><${Ico} d="check" w=${13}/></span>
          <span className="txt"><b style=${{ fontFamily:"var(--sans)", fontWeight:500 }}>${k}</b><span>${v}</span></span>
        </div>`)}
    <//>` : null}
  </div>`;
}

export default ModelSettingsDesk;
